import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { getUser, selectUser, selectUserState, userActions } from '../../store/entities/user';
import { userListActions } from '../../store/entities/userList';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { Select } from '../common/form/Select';
import { SubmitButton } from '../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { UserRoleSelect } from '../common/form/UserRoleSelect';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { CancelToken } from 'axios';
import { Grid } from '../common/grid/Grid';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { EstablishmentFilter } from '../establishment/EstablishmentFilter';
import { QcsBox } from '../common/basic/QcsBox';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { employeeListActions } from '../../store/entities/employeeList';
import { PhoneInput } from '../common/form/PhoneInput';
import { getFirstLastNameObj, getLangNameObj } from '../../utils/format';
import { useNavigation } from '../../utils/urlPaths';
import { allEstablishmentListActions, getAllEstablishmentList, selectAllEstablishmentList } from '../../store/entities/allEstablishmentList';
import { resources } from '@api/resources';
import { applicationInvitationService, userService } from '@api/services/services';
import { EstablishmentDto } from '@api/models/establishment/EstablishmentDto';
import { EntityState } from '@api/models/enums/EntityState';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { invitationListActions } from '../../store/entities/invitationList';
import { visitationListActions } from '../../store/entities/visitationList';
import { entryRequestListActions } from '../../store/entities/entryRequestList';
import { workplaceListActions } from '../../store/entities/workplaceList';
import { establishmentListActions } from '../../store/entities/establishmentList';
import { ShowResourceContent } from '../ShowResourceContent';
import { UserRequest } from '@api/models/user/UserRequest';
import { ShowError } from '../ShowError';

export const UserDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const { goBack, getBackPath: backTo } = useNavigation('/user');
  const { userId } = useParams();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const user = useAppSelector(selectUser);
  const establishmentList = useAppSelector(selectAllEstablishmentList);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userId === 'new') {
      dispatch(userActions.default());
    } else {
      dispatch(getUser(userId!));
    }
  }, [userId, dispatch]);

  const handleSubmit = async (data: UserRequest) => {
    await userService.update(userId!, data);
    dispatch(userListActions.reload(true));
    dispatch(employeeListActions.reload(true));
    dispatch(invitationListActions.reload(true));
    dispatch(visitationListActions.reload(true));
    dispatch(entryRequestListActions.reload(true));
    dispatch(workplaceListActions.reload(true));
    dispatch(establishmentListActions.reload(true));
    goBack();
  };

  const handleResendInvitation = async () => {
    await applicationInvitationService.resendApplicationInvitation(userId!);
  };

  const handleEstablishmentGetData = (cancelToken: CancelToken) => {
    dispatch(getAllEstablishmentList(cancelToken));
  };

  const handleIsRowDisabled = (item: EstablishmentDto) => item.state !== EntityState.Active;

  const handleEstablishmentRenderData = (item: EstablishmentDto) => {
    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{getFirstLastNameObj(item.contact)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.contact.email}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.contact.phone}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{t('entityState.' + item.state)}</QcsTableCell>
      </>
    );
  };

  const validate = (values: UserRequest) => {
    const errors: Partial<Record<keyof UserRequest, string>> = {};

    if (values.roles.some((role) => role === UserRole.Tourniquet || role === UserRole.TourniquetTraining) && values.roles.length !== 1) {
      errors.roles = t('user.errorInvalidRoleCombination') ?? undefined;
    }

    return errors;
  };

  return (
    <>
      <DetailHeader title="user.detailTitle" backTo={backTo} />
      <ShowResourceContent resource={resources.getUser} fetchStateSelector={selectUserState}>
        <FormikDebounce<UserRequest>
          initialValues={{
            id: user.id,
            name: user.name,
            lastName: user.lastName,
            email: user.email ?? '',
            state: user.state,
            phone: user.phone,
            establishments: user.establishments,
            note: user.note,
            office: user.office,
            language: user.language ?? '',
            roles: user.roles,
          }}
          validationSchema={Yup.object().shape({
            name: validations.stringRequired(t),
            lastName: validations.stringRequired(t),
            roles: validations.arrayNotEmpty(t),
            language: validations.stringRequired(t),
            phone: validations.phoneOptional(t),
            email: validations.emailRequired(t),
          })}
          validate={validate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <FormContainer>
                <Input name="name" label={t('user.name')} maxLength={100} required />
                <Input name="lastName" label={t('user.lastName')} maxLength={100} required />
                <UserRoleSelect
                  name="roles"
                  label={t('user.roles')}
                  required
                  onChange={(e) => {
                    const newRoles: string[] = e.target.value;
                    const hasHwTurnstile = newRoles.includes(UserRole.HwTurnstile);
                    // Clear establishments when more than one establishment is selected and the user selects the HwTurnstile role
                    if (hasHwTurnstile && (values.establishments?.length ?? 0) > 1) setFieldValue('establishments', []);
                  }}
                />
                <PhoneInput name="phone" label={t('user.phone')} />
                <Input name="email" removeSpaces label={t('user.email')} maxLength={100} required />

                {user.state === UserState.Invited && (
                  <QcsBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <QcsLoadingButton variant="contained" onClick={handleResendInvitation} resourceLoading={resources.resendApplicationInvitation}>
                      {t('user.inviteUser')}
                    </QcsLoadingButton>
                  </QcsBox>
                )}

                <div>
                  <QcsTypography variant="h6">{t('user.detailEstablishmentSelect')}:</QcsTypography>
                  <Grid<EstablishmentDto>
                    headers={[
                      { captionStr: 'establishment.name' },
                      {
                        captionStr: 'establishment.contactName',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.email',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.phone',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.state',
                        hideOnMobile: true,
                      },
                    ]}
                    //establishmentList is used by Establishment.tsx - must have same filter (actual) or split store
                    data={establishmentList}
                    gridActions={allEstablishmentListActions}
                    renderData={handleEstablishmentRenderData}
                    isRowDisabled={handleIsRowDisabled}
                    getData={handleEstablishmentGetData}
                    checkbox={true}
                    singleSelect={values.roles.includes(UserRole.HwTurnstile)}
                    checkedItems={values.establishments}
                    changeChecked={(items) => setFieldValue('establishments', items)}
                    filter={<EstablishmentFilter query={establishmentList.query} />}
                  />
                </div>
                <Input name="office" label={t('user.office')} maxLength={100} />
                <LanguageSelect
                  name="language"
                  label={t('user.language')}
                  languages={SUPPORTED_LANGUAGES}
                  allowedLanguages={companyCustomization?.applicationLanguages}
                  required
                />
                <Select name="state" label={t('user.state')}>
                  <QcsMenuItem value={UserState.Active} disabled={user.state === UserState.Invited}>
                    {t('entityState.ACTIVE')}
                  </QcsMenuItem>
                  <QcsMenuItem value={UserState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                  <QcsMenuItem value={UserState.Invited} disabled={user.state !== UserState.Invited}>
                    {t('entityState.INVITED')}
                  </QcsMenuItem>
                </Select>
                <Input name="note" label={t('user.note')} maxLength={250} multiline={true} rows={4} />
                <ShowError resource={[resources.createQcsUser, resources.updateUser]} />
                <SubmitButton />
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
